
import { FilterRule } from '@/types/adjustment-filters';
import { evaluateFilterRules } from '@/utils/adjustmentFilterUtils';
import { supabase } from '@/lib/supabase';

/**
 * Represents the minimal line item shape needed for total calculation.
 * Supports both live DB rows and version snapshot formats.
 */
export interface LineItemForCalculation {
  id?: string;
  total_cost_snapshot: number;
  manual_override?: boolean;
  manual_total_cost?: number | null;
  component_id?: string;
  component_type?: string;
  category_id?: string;
  quantity_in_base_units?: number;
  // Snapshot format aliases
  total_cost?: number;
  quantity?: number;
}

export interface AdjustmentMetadataForCalculation {
  filters?: FilterRule[];
  discountPercentage?: number;
}

export interface BaseCostEntry {
  base_cost: number;
  category_id?: string;
  trade_id?: string;
}

export interface AdjustedTotalResult {
  /** Sum of line items after markup removal but before discount */
  subtotal: number;
  /** Discount amount */
  discountAmount: number;
  /** Final subtotal after discount (before VAT) */
  adjustedSubtotal: number;
}

/**
 * Pure, synchronous function to compute the adjusted estimate total.
 * This is the SINGLE SOURCE OF TRUTH for estimate total calculation.
 * 
 * Priority per item:
 *   1. Manual override → use manual_total_cost
 *   2. Filter match + baseCostMap entry → use base_cost × quantity (markup removed)
 *   3. Otherwise → use total_cost_snapshot
 * 
 * Then applies discount percentage to the sum.
 */
export const computeAdjustedEstimateTotal = (
  lineItems: LineItemForCalculation[],
  adjustmentMetadata?: AdjustmentMetadataForCalculation | null,
  baseCostMap?: Map<string, BaseCostEntry>
): AdjustedTotalResult => {
  if (!lineItems || lineItems.length === 0) {
    return { subtotal: 0, discountAmount: 0, adjustedSubtotal: 0 };
  }

  const filters: FilterRule[] = adjustmentMetadata?.filters && Array.isArray(adjustmentMetadata.filters)
    ? adjustmentMetadata.filters
    : [];
  const discountPercentage = typeof adjustmentMetadata?.discountPercentage === 'number'
    ? adjustmentMetadata.discountPercentage
    : 0;
  const hasFilters = filters.length > 0;

  const subtotal = lineItems.reduce((sum, item) => {
    // 1. Manual override takes priority
    if (item.manual_override && item.manual_total_cost != null) {
      const val = typeof item.manual_total_cost === 'number'
        ? item.manual_total_cost
        : parseFloat(String(item.manual_total_cost)) || 0;
      return sum + val;
    }

    // 2. If filters exist and we have base cost data, check for markup removal
    if (hasFilters && baseCostMap && item.component_id) {
      const sourceData = baseCostMap.get(item.component_id);
      if (sourceData) {
        const quantity = item.quantity_in_base_units ?? item.quantity ?? 0;
        const matches = evaluateFilterRules(filters, {
          category_id: sourceData.category_id || item.category_id,
          trade_id: sourceData.trade_id,
          quantity_in_base_units: quantity,
          base_cost: sourceData.base_cost,
        });
        if (matches) {
          return sum + (sourceData.base_cost * quantity);
        }
      }
    }

    // 3. Fallback to snapshot (support both field names)
    const snapshotValue = item.total_cost_snapshot ?? item.total_cost ?? 0;
    const val = typeof snapshotValue === 'number'
      ? snapshotValue
      : parseFloat(String(snapshotValue)) || 0;
    return sum + val;
  }, 0);

  const discountAmount = discountPercentage > 0 ? subtotal * (discountPercentage / 100) : 0;
  const adjustedSubtotal = subtotal - discountAmount;

  return { subtotal, discountAmount, adjustedSubtotal };
};

/**
 * Builds a baseCostMap by batch-fetching from supplier_line_items and internal_line_items.
 * Use this when you have line items but no pre-built baseCostMap.
 */
export const fetchBaseCostMap = async (
  lineItems: LineItemForCalculation[]
): Promise<Map<string, BaseCostEntry>> => {
  const baseCostMap = new Map<string, BaseCostEntry>();

  const supplierIds: string[] = [];
  const internalIds: string[] = [];

  for (const item of lineItems) {
    if (item.component_type === 'supplier_line_item' && item.component_id) {
      supplierIds.push(item.component_id);
    } else if (item.component_type === 'internal_line_item' && item.component_id) {
      internalIds.push(item.component_id);
    }
  }

  const queries: Promise<void>[] = [];

  if (supplierIds.length > 0) {
    queries.push(
      (async () => {
        const { data } = await (supabase as any)
          .from('supplier_line_items')
          .select('id, base_cost, category_id, trade_id')
          .in('id', supplierIds);
        for (const d of data || []) {
          baseCostMap.set(d.id, {
            base_cost: d.base_cost,
            category_id: d.category_id || undefined,
            trade_id: d.trade_id || undefined,
          });
        }
      })()
    );
  }

  if (internalIds.length > 0) {
    queries.push(
      (async () => {
        const { data } = await supabase
          .from('internal_line_items')
          .select('id, base_cost, category_id')
          .in('id', internalIds);
        for (const d of data || []) {
          baseCostMap.set(d.id, {
            base_cost: d.base_cost,
            category_id: d.category_id || undefined,
          });
        }
      })()
    );
  }

  await Promise.all(queries);
  return baseCostMap;
};

/**
 * Async convenience: fetches base costs and computes adjusted total in one call.
 */
export const computeAdjustedEstimateTotalAsync = async (
  lineItems: LineItemForCalculation[],
  adjustmentMetadata?: AdjustmentMetadataForCalculation | null
): Promise<AdjustedTotalResult> => {
  const hasFilters = adjustmentMetadata?.filters && adjustmentMetadata.filters.length > 0;

  let baseCostMap: Map<string, BaseCostEntry> | undefined;
  if (hasFilters) {
    baseCostMap = await fetchBaseCostMap(lineItems);
  }

  return computeAdjustedEstimateTotal(lineItems, adjustmentMetadata, baseCostMap);
};
